import { Injectable, signal, WritableSignal } from '@angular/core';
import { ApiService } from '@services/api.service';
import { Observable, tap } from 'rxjs';
import { ApiRequest } from '@enums/api.enum';
import {
	AlertRule,
	AlertRuleListResponse,
	AlertHistoryFilter,
	AlertHistoryListResponse,
	AlertHistoryEntry,
	CreateAlertRuleRequest,
	NotificationPreferences,
	RuleStatsResponse,
	NotificationsOverview,
	PriceLevelsResponse,
	UpdateAlertRuleRequest,
} from '@models/alert';

@Injectable({
	providedIn: 'root',
})
export class AlertManagerService {
	rules: WritableSignal<AlertRule[]> = signal<AlertRule[]>([]);
	history: WritableSignal<AlertHistoryEntry[]> = signal<AlertHistoryEntry[]>([]);
	unreadCount: WritableSignal<number> = signal(0);
	totalHistory: WritableSignal<number> = signal(0);
	loading: WritableSignal<boolean> = signal(false);

	constructor(private _api: ApiService) {}

	// ── Rules CRUD ──

	getRules(): Observable<AlertRuleListResponse> {
		this.loading.set(true);
		return this._api.sendGET<AlertRuleListResponse>(ApiRequest.AlertRules).pipe(
			tap((res) => {
				this.rules.set(res.rules);
				this.loading.set(false);
			}),
		);
	}

	createRule(req: CreateAlertRuleRequest): Observable<AlertRule> {
		return this._api.sendPOST<AlertRule>(ApiRequest.AlertRules, req).pipe(
			tap((rule) => {
				this.rules.update((list) => [rule, ...list]);
			}),
		);
	}

	updateRule(ruleId: string, req: UpdateAlertRuleRequest): Observable<AlertRule> {
		return this._api.sendPUT<AlertRule>(`${ApiRequest.AlertRules}/${ruleId}`, req).pipe(
			tap((updated) => {
				this.rules.update((list) => list.map((r) => (r.id === updated.id ? updated : r)));
			}),
		);
	}

	deleteRule(ruleId: string): Observable<{ message: string }> {
		return this._api.sendDELETE<{ message: string }>(`${ApiRequest.AlertRules}/${ruleId}`).pipe(
			tap(() => {
				this.rules.update((list) => list.filter((r) => r.id !== ruleId));
			}),
		);
	}

	// ── History (feed) ──

	getHistory(
		limit = 50,
		offset = 0,
		filter: AlertHistoryFilter = {},
	): Observable<AlertHistoryListResponse> {
		const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });

		if (filter.source) {
			q.set('source', filter.source);
		}

		if (filter.symbol) {
			q.set('symbol', filter.symbol);
		}

		if (filter.severity) {
			q.set('severity', filter.severity);
		}

		if (filter.unread) {
			q.set('unread', 'true');
		}

		return this._api
			.sendGET<AlertHistoryListResponse>(`${ApiRequest.AlertHistory}?${q.toString()}`)
			.pipe(
				tap((res) => {
					this.history.set(res.alerts);
					this.totalHistory.set(res.total);
					this.unreadCount.set(res.unread);
				}),
			);
	}

	markRead(ids: string[]): Observable<{ message: string }> {
		return this._api.sendPOST<{ message: string }>(ApiRequest.AlertHistoryRead, { ids }).pipe(
			tap(() => {
				this.history.update((list) =>
					list.map((a) =>
						ids.includes(a.id) ? { ...a, read_at: new Date().toISOString() } : a,
					),
				);
				this.unreadCount.update((n) => Math.max(0, n - ids.length));
			}),
		);
	}

	markAllRead(): Observable<{ message: string }> {
		return this._api.sendPOST<{ message: string }>(ApiRequest.AlertHistoryReadAll, {}).pipe(
			tap(() => {
				this.history.update((list) =>
					list.map((a) => ({ ...a, read_at: a.read_at ?? new Date().toISOString() })),
				);
				this.unreadCount.set(0);
			}),
		);
	}

	/** Delete the given entries, or (no ids) every already-read entry. */
	deleteHistory(ids?: string[]): Observable<{ message: string }> {
		return this._api
			.sendPOST<{ message: string }>(ApiRequest.AlertHistoryDelete, ids ? { ids } : {})
			.pipe(
				tap(() => {
					this.history.update((list) =>
						ids
							? list.filter((a) => !ids.includes(a.id))
							: list.filter((a) => !a.read_at),
					);
				}),
			);
	}

	// ── Overview + delivery settings ──

	getOverview(): Observable<NotificationsOverview> {
		return this._api
			.sendGET<NotificationsOverview>(ApiRequest.AlertOverview)
			.pipe(tap((o) => this.unreadCount.set(o.unread)));
	}

	/** Per-rule counts + the coins behind the favourites / watchlist scopes. */
	getRuleStats(): Observable<RuleStatsResponse> {
		return this._api.sendGET<RuleStatsResponse>(ApiRequest.AlertRuleStats);
	}

	/** `at` (unix s) = the levels as they stood then — replays a past event (alert films). */
	getPriceLevels(symbol: string, at?: number | null): Observable<PriceLevelsResponse> {
		return this._api.sendGET<PriceLevelsResponse>(
			`${ApiRequest.AlertPriceLevels}?symbol=${encodeURIComponent(symbol)}` +
				(at ? `&at=${at}` : ''),
		);
	}

	getPreferences(): Observable<NotificationPreferences> {
		return this._api.sendGET<NotificationPreferences>(ApiRequest.NotificationPreferences);
	}

	savePreferences(p: NotificationPreferences): Observable<NotificationPreferences> {
		return this._api.sendPUT<NotificationPreferences>(ApiRequest.NotificationPreferences, p);
	}
}
